import { useState, useEffect, useRef, useMemo } from "react";
import { motion, useInView } from "framer-motion";

/**
 * A styled segment within typewriter text.
 * Plain strings render as-is; objects apply optional styles.
 * Use '\n' strings for responsive line breaks.
 */
export type TypewriterSegment = string | { text: string; className?: string };

export interface TypewriterProps {
  /** Array of text segments to type out in order. */
  segments: TypewriterSegment[];
  /** Callback when typing finishes. */
  onComplete?: () => void;
  /** Base typing speed in ms per character (default: 32). */
  speed?: number;
  /** Initial delay before typing starts in ms (default: 600). */
  startDelay?: number;
  /** Show a blinking cursor while typing (default: true). */
  cursor?: boolean;
  /** Cursor color class (default: 'bg-fwd-yellow'). */
  cursorClass?: string;
  /** Class applied to line-break <br> elements (default: 'hidden md:block'). */
  breakClass?: string;
}

export const Typewriter = ({
  segments,
  onComplete,
  speed = 32,
  startDelay = 600,
  cursor = true,
  cursorClass = "bg-fwd-yellow",
  breakClass = "hidden md:block",
}: TypewriterProps) => {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const isInView = useInView(wrapRef, { once: true, amount: 0.3 });

  const flatChars = useMemo(
    () =>
      segments.flatMap((seg) => {
        const str = typeof seg === "string" ? seg : seg.text;
        return Array.from(str).map((ch) => ({ ch, seg }));
      }),
    [segments],
  );
  const totalChars = flatChars.length;

  const [visible, setVisible] = useState(0);
  const [, setDone] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!isInView) return;

    let cur = 0;
    let tid: number;
    let stopped = false;

    const next = () => {
      if (stopped) return;
      cur++;
      setVisible(cur);
      if (cur >= totalChars) {
        setDone(true);
        setTimeout(() => {
          if (!stopped) onCompleteRef.current?.();
        }, 200);
        return;
      }
      const { ch } = flatChars[cur - 1];
      let delay: number;
      if (ch === "\n") {
        delay = 180 + Math.random() * 100;
      } else if (",.:;".includes(ch)) {
        delay = 120 + Math.random() * 80;
      } else {
        delay = speed + Math.random() * (speed * 0.75);
        if (Math.random() < 0.07) delay += 50 + Math.random() * 60;
      }
      tid = window.setTimeout(next, delay);
    };

    tid = window.setTimeout(next, startDelay);
    return () => {
      stopped = true;
      clearTimeout(tid);
    };
  }, [isInView, totalChars, flatChars, speed, startDelay]);

  let charIdx = 0;
  const rendered = segments.map((seg, si) => {
    const str = typeof seg === "string" ? seg : seg.text;
    const chars = Array.from(str);
    const isStyled = typeof seg !== "string";

    const fragment = chars.map((ch, ci) => {
      const idx = charIdx++;
      if (idx >= visible) return null;
      if (ch === "\n") return <br key={`${si}-${ci}`} className={breakClass} />;
      return <span key={`${si}-${ci}`}>{ch}</span>;
    });

    if (isStyled) {
      return (
        <span key={si} className={seg.className}>
          {fragment}
        </span>
      );
    }
    return <span key={si}>{fragment}</span>;
  });

  return (
    <span ref={wrapRef}>
      {rendered}
      {cursor && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{
            duration: 0.5,
            repeat: Infinity,
            repeatType: "reverse",
          }}
          className={`inline-block w-[3px] h-[0.85em] ${cursorClass} ml-1 align-baseline translate-y-[0.1em]`}
        />
      )}
    </span>
  );
};
